import { useMutation } from '@tanstack/react-query';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import { buildStudentViewUrl, isStudentViewUserId } from './studentViewSession';

interface OpenStudentViewInput {
  courseId: string;
  // Optional content id: open student view directly on a component page.
  contentId?: string;
}

interface GetOrCreateStudentViewUserPayload {
  course_id: string;
  user_id: string;
}

interface RawStudentViewUserResponse {
  student_view_user_id?: string;
  studentViewUserId?: string;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function unwrapStudentViewUserId(response: unknown): string | undefined {
  if (typeof response !== 'object' || response === null) {
    return undefined;
  }

  const record =
    'Response' in response
      ? (response as LegacyKuduResponseEnvelope<RawStudentViewUserResponse>)
          .Response
      : (response as RawStudentViewUserResponse);

  const id = record?.student_view_user_id ?? record?.studentViewUserId;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

// The server's own text never reaches the page (docs/engineering/frontend.md):
// a failed request becomes words of its own, keeping its kind.
function describeOpenFailure(error: unknown): ApiError {
  const kind = (error as Partial<ApiError> | null)?.kind ?? 'data';
  const described = new Error(
    kind === 'permission'
      ? 'Kudu refused to open the student view. Reload the page and try again.'
      : kind === 'network'
        ? 'We could not reach the server. Check your connection and try again.'
        : kind === 'auth'
          ? 'You must be signed in to open student view.'
          : 'Something went wrong opening the student view tab.',
  ) as ApiError;
  described.kind = kind;
  return described;
}

/**
 * Instructor-side launcher: get-or-create the per-instructor test account for
 * the course (server-derived reserved id), then open student view in a NEW
 * TAB. The opened tab picks the identity up from the URL parameters and keeps
 * it in ITS sessionStorage — the instructor's tab and login are untouched.
 */
export function useOpenStudentView() {
  const { getIdToken, user } = useAuth();

  return useMutation<void, ApiError, OpenStudentViewInput>({
    mutationFn: async ({ contentId, courseId }) => {
      const kuduUserId = getKuduUserId(user);

      if (!user || !kuduUserId) {
        throw createDataError('You must be signed in to open student view.');
      }

      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () => {
          window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`);
        },
      });

      let response: unknown;
      try {
        response = await client.invokeKuduMethod<
          unknown | LegacyKuduResponseEnvelope<RawStudentViewUserResponse>,
          GetOrCreateStudentViewUserPayload
        >('GetOrCreateStudentViewUser', {
          course_id: courseId,
          user_id: kuduUserId,
        });
      } catch (error) {
        throw describeOpenFailure(error);
      }

      const studentViewUserId = unwrapStudentViewUserId(response);

      if (!studentViewUserId || !isStudentViewUserId(studentViewUserId, courseId)) {
        throw createDataError('Unable to prepare the student view account.');
      }

      const path = contentId
        ? `/app/courses/${courseId}/${contentId}`
        : `/app/courses/${courseId}`;
      // Open WITHOUT the 'noopener' feature: with it, window.open returns null
      // on success too (per spec), which is indistinguishable from a blocked
      // pop-up. Instead detect a genuine block (null) and then sever the opener
      // ourselves — the target is our own same-origin app, so this restores the
      // no-opener security property.
      const opened = window.open(
        buildStudentViewUrl(path, { courseId, userId: studentViewUserId }),
        '_blank',
      );

      if (!opened) {
        throw createDataError(
          'Your browser blocked the student view tab. Allow pop-ups for this site and try again.',
        );
      }

      try {
        opened.opener = null;
      } catch {
        // A cross-origin or restricted window may forbid this; the security
        // benefit is best-effort and the tab already opened successfully.
      }
    },
  });
}
